import * as React from 'react';
import clsx from 'clsx';

import {useTranslation} from 'common/i18n/hooks';
import {reporter} from 'common/report/error-reporting';

import {WarningReact} from '../warning';
import {WarningTheme} from '../warning/constants';
import style from './index.module.styl';

type FallbackProps = {
  fullScreen?: boolean;
  minimal?: boolean;
};

function Fallback({fullScreen, minimal}: FallbackProps) {
  const {t} = useTranslation();
  return (
    <WarningReact
      {...{
        theme: WarningTheme.Error,
        headerText: minimal ? undefined : t(`error-boundary.header`),
        bodyText: minimal ? t(`error-boundary.body-minimal`) : t(`error-boundary.body`),
        small: minimal,
        className: clsx(style.fillContainer, {[style.fullScreen]: fullScreen}),
      }}
    />
  );
}

type ReactErrorBoundaryProps = React.PropsWithChildren & {
  fallback?: (error: any) => React.ReactNode;
  fullScreen?: boolean;
  minimal?: boolean;
  errorType?: string;
};

type ReactErrorBoundaryState = {
  error: any; // JS really lets you throw anything
};

export class ReactErrorBoundary extends React.Component<ReactErrorBoundaryProps, ReactErrorBoundaryState> {
  state: ReactErrorBoundaryState = {error: null};

  static getDerivedStateFromError(error: Error) {
    return {error};
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    reporter.error(error, errorInfo, this.props.errorType);
  }

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error);
      }
      return <Fallback fullScreen={this.props.fullScreen} minimal={this.props.minimal} />;
    }
    return this.props.children;
  }
}
